// src/components/CartContent/index.js - COMPLETE FIXED VERSION
// ============================================
// SCENARIO MATRIX IMPLEMENTATION:
// ============================================
// Store | Pre-Order | Order Type | Behavior
// ------+-----------+------------+------------------------------------------
// OPEN  | NO        | Delivery   | ASAP only (30-45 min from now)
// OPEN  | NO        | Collection | Today's time slots only (no schedule toggle)
// OPEN  | YES       | Delivery   | ASAP + "Schedule for Later" toggle
// OPEN  | YES       | Collection | ASAP + "Schedule for Later" toggle  
// CLOSED| NO        | Any        | Cannot proceed (blocked at store selector)
// CLOSED| YES       | Any        | Schedule for Later only (no ASAP option)
// ============================================

import React, { useState, useEffect, useRef } from 'react';
import { getRestaurantConfigSync } from '../../config/restaurantConfig';
import { Plus, Minus, Clock, MapPin, Gift, ChevronDown, Edit2, ChevronUp, Percent, TrendingUp, Truck, ShoppingBag, Tag, AlertCircle, DollarSign, Calendar, Zap, MapPinOff, Trash2 } from 'lucide-react';
import TimePicker from '../TimePicker';
import SpiceLevel from '../SpiceLevel';
// ADD THIS IMPORT at the top of CartContent/index.js:
import useAddressManager from '../Useaddressmanager/useAddressManager';
import AddressModal from '../Addressmodal';
import './index.css';
import { settingsService } from '../../config/settingsService';
import { calculateDistance } from '../../utils/storeUtils';
import { formatLocalDateTime } from '../../utils/timeSlotGenerator';
import { CHECKOUT_FLOW_CONFIG } from '../../config/checkoutConfig';
import FallbackImg from '../FallbackImg';
import { calculateCartTaxes } from '../../utils/taxCalculator';

const formatPrice = (amount) => {
  return settingsService.formatPrice(amount);
};

// Outlet types where "special requests" (cooking instructions) make sense.
// Apparel/grocery/salon orders have no kitchen to instruct, so the section
// is hidden entirely for those rather than shown with an irrelevant prompt.
const INSTRUCTIONS_PLACEHOLDER_BY_OUTLET_TYPE = {
  restaurant: 'Add any special requests for your meal...',
  cafe_coffee_shop: 'Add any special requests for your order...',
  fast_food_qsr: 'Add any special requests for your order...',
  cloud_kitchen: 'Add any special requests for your meal...',
  bakery: 'Add any special requests for your bakery order...',
  sweet_shop: 'Add any special requests for your sweets order...',
  dry_fruit_shop: 'Add any special requests for your order...',
  bar_restaurant: 'Add any special requests for your order...'
};



const CartContent = ({
  cart,
  categories = [],
  cartAnalysis,
  onUpdateQuantity,
  onRemove,
  onPlaceOrder,
  loading,
  getTotalAmount,
  getTotalSavings,
  getFinalTotal,
  getFreeItemsValue,
  orderType,
  selectedTime,
  onTimeChange,
  selectedAddress,
  onAddressChange,
  offersManager,
  selectedStore,
  isPreOrder = false,
  scheduleInfo = null,
  showScheduleUI = true,
  storeIsOpen = true,
  onEditCustomization,
  userId = null,
  cookingInstruction = '',
  setCookingInstruction = () => { },
  // ✅ Passed from Central App Hook
  addresses = [],
  addressesLoading = false,
  onAddressSelect = () => { },
  onCreateAddress = () => { },
  onUpdateAddress = () => { },
  onDeleteAddress = () => { },
  onSetDefaultAddress = () => { },
  shippingInfo = null,
  // Reports the customer's Air/Surface pick up to the parent, which re-runs /web/cart/analyze so
  // the delivery fee below is the one for that speed.
  onDeliverySpeedChange = () => { },
  isAnalyzing = false,
  outletType = 'restaurant',
  // ✅ Reports the current ASAP/Schedule-for-Later choice up to the parent —
  // needed so RestaurantMenuApp's order payload can tell the two apart
  // (both populate selectedTime, ASAP via the computed ASAP time below).
  // No UI change: purely an additional notification of state that already
  // exists locally in this component.
  onScheduleModeChange = () => { },
  // ✅ Redeem Points — { points: { available, ... } } from the cached
  // /web/me profile (read in RestaurantMenuApp, not fetched again here).
  // null/undefined until loaded or when the customer isn't logged in.
  loyaltyPoints = null,
  redeemPoints = 0,
  onRedeemPointsChange = () => { }
}) => {
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  // Raw text in the Redeem Points input. Kept local so the customer can type
  // an amount below the minimum and see a hint, without that half-entered
  // value being sent up / applied. Only a valid amount is reported to the
  // parent via onRedeemPointsChange. (Sync-down effect lives further below,
  // after minRedeemPoints is derived.)
  const [redeemInput, setRedeemInput] = useState('');

  // Custom scrollbar thumb for .cart-scrollable-content — the real
  // scrolling element for the cart body (items, delivery time,
  // instructions, bill summary). No browser lets CSS give a native
  // scrollbar thumb a fixed/short height (confirmed by direct testing
  // elsewhere in this app), so a short thumb needs a real custom one,
  // matching the Categories sidebar / product grid pattern. Native
  // scrolling (wheel/trackpad/touch) is unaffected — .cart-scrollable-
  // content-scroll below is still the real, natively-scrolling element;
  // only its default scrollbar chrome is hidden in favor of this
  // custom indicator.
  const cartScrollRef = useRef(null);
  const cartThumbRef = useRef(null);
  const cartTrackRef = useRef(null);
  const CART_THUMB_HEIGHT = 48;

  useEffect(() => {
    const el = cartScrollRef.current;
    const thumb = cartThumbRef.current;
    const track = cartTrackRef.current;
    if (!el || !thumb || !track) return undefined;
    let hideTimer;

    const updateThumb = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const maxScrollTop = scrollHeight - clientHeight;
      if (maxScrollTop <= 1) {
        thumb.style.display = 'none';
        return;
      }
      thumb.style.display = 'block';
      const trackHeight = track.clientHeight;
      const thumbHeight = Math.min(CART_THUMB_HEIGHT, trackHeight);
      const travel = Math.max(trackHeight - thumbHeight, 0);
      const thumbTop = (scrollTop / maxScrollTop) * travel;
      thumb.style.height = `${thumbHeight}px`;
      thumb.style.transform = `translateY(${thumbTop}px)`;
    };

    // Thumb stays hidden until scroll activity, then fades out again after
    // a short idle period — matches OS-style auto-hide scrollbars. Dragging
    // the thumb itself also fires native scroll events (see the pointerdown
    // handler below setting el.scrollTop), so this same listener keeps it
    // visible through a drag too, without needing separate logic there.
    const handleScroll = () => {
      updateThumb();
      thumb.classList.add('is-visible');
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => thumb.classList.remove('is-visible'), 1000);
    };

    updateThumb();
    el.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateThumb);

    // Cart content height changes constantly (items added/removed,
    // address/offers sections expanding) — a ResizeObserver catches all
    // of that without needing every state value that could affect it as
    // a dep.
    const resizeObserver = new ResizeObserver(updateThumb);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateThumb);
      resizeObserver.disconnect();
      clearTimeout(hideTimer);
    };
  }, []);

  const handleCartThumbPointerDown = (e) => {
    const el = cartScrollRef.current;
    const track = cartTrackRef.current;
    if (!el || !track) return;
    e.preventDefault();
    const startY = e.clientY;
    const startScrollTop = el.scrollTop;
    const maxScrollTop = el.scrollHeight - el.clientHeight;
    const trackHeight = track.clientHeight;
    const thumbHeight = Math.min(CART_THUMB_HEIGHT, trackHeight);
    const travel = Math.max(trackHeight - thumbHeight, 0);

    const handlePointerMove = (moveEvent) => {
      if (travel <= 0) return;
      const deltaY = moveEvent.clientY - startY;
      const scrollDelta = (deltaY / travel) * maxScrollTop;
      el.scrollTop = Math.min(Math.max(startScrollTop + scrollDelta, 0), maxScrollTop);
    };
    const handlePointerUp = () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  // ============================================
  // FIXED: Simple schedule mode calculation (no memoization)
  // ============================================

  // Calculate conditions directly - these are cheap boolean operations
  const storeSupportsPreOrder = Boolean(selectedStore?.allow_pre_order || selectedStore?.pre_order_enabled);
  // ✅ Gap B fix: a closed store only forces "must schedule" when this store
  // actually supports pre-orders. A closed store with pre-order disabled has
  // no valid future time to schedule against, so it must NOT be treated as
  // schedulable — see the closed-and-unsupported empty state below instead.
  const mustSchedule = (!storeIsOpen && storeSupportsPreOrder) || (isPreOrder && orderType === 'collection');
  // COLLECTION orders should NOT show ASAP - always require time selection
  const canShowASAP = storeIsOpen && !isPreOrder && !storeSupportsPreOrder && orderType === 'delivery';
  const canShowScheduleToggle = storeIsOpen && (isPreOrder || storeSupportsPreOrder) && orderType === 'delivery';

  // Calculate initial mode (only runs once on mount)
  const getInitialMode = () => {
    if (mustSchedule) return 'later';
    // COLLECTION orders always require explicit time selection
    if (orderType === 'collection') return 'later';
    if (canShowASAP) return 'asap';
    if (canShowScheduleToggle) return 'asap';
    return 'later';
  };

  // Initialize state with calculated value
  const [scheduleMode, setScheduleMode] = useState(getInitialMode);

  // ✅ Notify the parent whenever the ASAP/Schedule-for-Later choice
  // changes — see onScheduleModeChange above for why this is needed.
  useEffect(() => {
    onScheduleModeChange(scheduleMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleMode]);

  // ============================================
  //  ASAP Time Calculation
  // ============================================


  const getASAPDeliveryTime = () => {
    const now = new Date(); // Always use fresh time
    const prepTime = settingsService.getFoodPreparationTime();
    const slotDuration = settingsService.getScheduleSlotDuration(); // Buffer time

    // Total Time = Prep Time + Slot Duration + 10 mins travel buffer
    const deliveryTime = new Date(now.getTime() + (prepTime + slotDuration + 10) * 60000);
    // Local time — the backend reads this string as the branch's timezone.
    // toISOString() (UTC) here shifted delivery_time back by the UTC offset.
    return formatLocalDateTime(deliveryTime);
  };



  const getASAPDisplayTime = () => {
    const now = new Date(); // Always use fresh time
    const prepTime = settingsService.getFoodPreparationTime();
    const slotDuration = settingsService.getScheduleSlotDuration();

    // Base Estimate = Prep Time + Slot Duration
    const totalMinutes = prepTime + slotDuration;

    // Range: Total Minutes to Total Minutes + 15 mins
    const minTime = new Date(now.getTime() + totalMinutes * 60000);
    const maxTime = new Date(now.getTime() + (totalMinutes + 15) * 60000);

    const formatTime = (date) => {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    };

    return `${formatTime(minTime)} - ${formatTime(maxTime)}`;
  };

  // ============================================
  //  FIXED: Reset time when order type changes to Collection
  // ============================================
  useEffect(() => {
    // When switching to Collection, always clear the time
    // User MUST manually select a time slot
    if (orderType === 'collection') {
      onTimeChange(null);
      setShowTimePicker(true);
    }
  }, [orderType]);

  // ============================================
  //  FIXED: Auto-set ASAP time when mode changes
  // NOTE: COLLECTION orders should NOT auto-set time
  // Users MUST manually select a time slot
  // ============================================
  // ============================================
  //  FIXED: Keep ASAP display fresh without triggering parent re-renders
  // ============================================
  const [timeTick, setTimeTick] = useState(Date.now());

  useEffect(() => {
    let intervalId;

    const updateASAPTime = () => {
      const asapTime = getASAPDeliveryTime();
      onTimeChange(asapTime);
    };

    if (scheduleMode === 'asap' && orderType !== 'collection') {
      // Set initial time immediately
      updateASAPTime();

      // Update local tick every minute to keep display fresh!
      // DO NOT call onTimeChange here, or it will re-render the entire RestaurantMenuApp
      intervalId = setInterval(() => setTimeTick(Date.now()), 60000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleMode, orderType]);

  // ============================================
  //  FIXED: Reset scheduling when props change
  // ============================================
  useEffect(() => {
    // Only force mode reset if store closure or orderType requires it
    if (mustSchedule && scheduleMode !== 'later') {
      setScheduleMode('later');
      setShowTimePicker(true);
      onTimeChange(null);
    } else if (orderType === 'collection' && scheduleMode !== 'later') {
      setScheduleMode('later');
      setShowTimePicker(true);
      onTimeChange(null);
    }
  }, [mustSchedule, orderType, onTimeChange]);



  // Removed local useAddressManager - now using props for central sync

  // Backwards compatibility/Internal aliases for the rest of the component
  const loadingAddresses = addressesLoading;
  const addressError = null;
  const operationInProgress = false;
  const selectAddress = onAddressSelect;
  const setDefaultAddress = onSetDefaultAddress;
  const createAddress = onCreateAddress;
  const updateAddress = onUpdateAddress;
  const deleteAddress = onDeleteAddress;
  const hasAddresses = addresses.length > 0;

  // Ensure sync if parent has selectedAddress but local doesn't (though they should be the same now)
  useEffect(() => {
    if (selectedAddress) {
      // Local state is now managed prop-style
    }
  }, [selectedAddress]);

  // ✅ Mobile logic: Handle back button for Address Modal
  useEffect(() => {
    if (!showAddressModal) return;

    window.history.pushState({ addressModal: true }, '');

    const handlePopState = (event) => {
      if (event.state?.addressModal) {
        return;
      }
      setShowAddressModal(false);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (window.history.state?.addressModal) {
        window.history.back();
      }
    };
  }, [showAddressModal]);

  const defaultAddress = addresses.find(addr => addr.isDefault) || addresses[0];

  // Delivery-time estimates are prep-time based, not distance-based — they
  // don't actually need an address to calculate. But showing an "Expected
  // delivery" time before the customer has picked where it's delivering to
  // reads as a promise the app can't back up yet, so for delivery orders we
  // hold off displaying it until a real address is selected.
  const hasDeliveryAddress = Boolean(selectedAddress || defaultAddress);


  const calculateItemPrice = (item) => {


    // 🎁 Handle combo items FIRST
    if (item.isCombo) {
      return parseFloat(item.flatPrice || item.price || 0);
    }

    const variations = Array.isArray(item.variations) ? item.variations : [];
    const extras = Array.isArray(item.extras) ? item.extras : [];
    const addons = Array.isArray(item.addons) ? item.addons : [];

    // CRITICAL FIX: Check if this item has been pre-calculated with customizations or is weight-based
    const hasCustomizations = variations.length > 0 || extras.length > 0 || addons.length > 0;

    // Fixed: Always use flatPrice for weight-based items, as it holds the calculated (weight * price per unit)
    if ((hasCustomizations || item.isWeightBased) && item.flatPrice && parseFloat(item.flatPrice) > 0) {
      return parseFloat(item.flatPrice);
    }

    // FALLBACK: Calculate from scratch
    const basePrice = parseFloat(item.price || 0);
    let itemPrice = basePrice;

    // STEP 1: Variations
    if (variations.length > 0) {
      const variationPrices = variations.map(v => parseFloat(v.variation_price || v.price || 0));
      const highestVariation = Math.max(...variationPrices);
      itemPrice = basePrice + highestVariation;
    }

    // STEP 2: Extras
    const extrasTotal = extras.reduce((sum, e) => {
      return sum + parseFloat(e.extra_price || e.price || 0);
    }, 0);
    if (extrasTotal > 0) {
      itemPrice += extrasTotal;
    }

    // STEP 3: Add Addons
    const addonsTotal = addons.reduce((sum, a) => {
      // ✅ FIXED: Use full price (including variations)
      return sum + parseFloat(a.total_flat_price || a.total || a.addon_item_price || a.price || 0);
    }, 0);
    if (addonsTotal > 0) {
      itemPrice += addonsTotal;
    }

    return itemPrice;
  };
  const getRecommendationIcon = (type) => {
    switch (type) {
      case 'free_delivery': return <Truck size={20} />;
      case 'discount_tier': return <TrendingUp size={20} />;
      case 'add_items': return <ShoppingBag size={20} />;
      case 'coupon': return <Tag size={20} />;
      case 'offer': return <Gift size={20} />;
      case 'savings': return <DollarSign size={20} />;
      default: return <AlertCircle size={20} />;
    }
  };

  const getRecommendationColor = (type) => {
    switch (type) {
      case 'free_delivery': return '#10b981';
      case 'discount_tier': return '#3b82f6';
      case 'add_items': return '#f59e0b';
      case 'coupon': return '#8b5cf6';
      case 'offer': return '#ec4899';
      case 'savings': return '#06b6d4';
      default: return '#6b7280';
    }
  };

  const formatCurrency = (amount) => {
    return settingsService.formatPrice(amount);
  };

  const recommendations = cartAnalysis?.recommendations || [];

  // Check if item has customizations
  const hasCustomizations = (item) => {
    return (
      (item.variations && item.variations.length > 0) ||
      (item.extras && item.extras.length > 0) ||
      (item.addons && item.addons.length > 0)
    );
  };

  //  FIXED: Calculate correct item total in Bill Summary
  // Instead of relying on parent's getTotalAmount, calculate based on cart items directly
  const calculateBillSummaryTotal = React.useMemo(() => {
    return cart.reduce((total, item) => {
      const itemPrice = calculateItemPrice(item);
      const itemTotal = itemPrice * item.quantity;
      return total + itemTotal;
    }, 0);
  }, [cart]); // Only recalculate when cart changes

  const analyzedCartStatus = cartAnalysis?.universal_response?.cart_status || null;
  const analyzedSubtotal = Number(analyzedCartStatus?.subtotal);
  const analyzedTaxTotal = Number(analyzedCartStatus?.tax_total);
  const analyzedGrandTotal = Number(analyzedCartStatus?.grand_total);
  // ✅ Backend-computed loyalty discount for the points the customer entered
  // (only present once /web/cart/analyze is sent loyalty_points_used). Read
  // defensively across the likely response shapes; the analyzed grand_total
  // above already has this subtracted, so this value is display-only.
  const analyzedLoyaltyDiscount = Number(
    analyzedCartStatus?.loyalty_discount ??
    analyzedCartStatus?.loyalty?.loyalty_discount ??
    cartAnalysis?.loyalty?.loyalty_discount ??
    cartAnalysis?.loyalty_discount ??
    0
  ) || 0;

  // Points are redeemed as whole numbers — floor the balance for display and
  // for the input ceiling (a 257.35 balance can spend at most 257).
  const availablePoints = Math.floor(Number(loyaltyPoints?.points?.available) || 0);

  // Minimum points per redemption. From /web/me when the backend exposes it;
  // otherwise falls back to 20 (this tenant's configured Minimum Redemption,
  // shown in the POS) — same "backend value else sensible default" approach
  // used for redemptionRate above.
  const MIN_REDEEM_FALLBACK = 20;
  const minRedeemPoints = Number(loyaltyPoints?.min_redemption) > 0
    ? Number(loyaltyPoints.min_redemption)
    : MIN_REDEEM_FALLBACK;

  // What the customer has typed vs. what's actually being applied (0 until it
  // clears the minimum). redeemPoints (the parent prop) is always the applied
  // amount — the only value sent to /web/cart/analyze and /web/order.
  const typedRedeemPoints = parseInt(redeemInput, 10) || 0;
  const enteredRedeemPoints = Number(redeemPoints) > 0 ? Number(redeemPoints) : 0;
  const redeemBelowMinimum = typedRedeemPoints > 0 && typedRedeemPoints < minRedeemPoints;

  // Clear the local input when the parent drops the applied points while the
  // input still holds a *valid* (>= min) amount — i.e. an external reset such
  // as a completed order. A below-min value is the customer mid-edit, leave it.
  useEffect(() => {
    if (!redeemPoints && typedRedeemPoints >= minRedeemPoints) {
      setRedeemInput('');
    }
  }, [redeemPoints, typedRedeemPoints, minRedeemPoints]);

  // ₹ value of one point. Uses the backend's configured rate when /web/me
  // exposes it; otherwise assumes 1 point = ₹1 (the common default, and what
  // this tenant's POS uses). Only ever drives the cart's on-screen ESTIMATE —
  // the real discount is always computed server-side at order creation.
  const redemptionRate = Number(loyaltyPoints?.redemption_rate) > 0
    ? Number(loyaltyPoints.redemption_rate)
    : 1;

  // Discount to SHOW and to subtract from the displayed total:
  //  - if /web/cart/analyze already returned one, trust that (grand_total
  //    is already reduced by it, so it's display-only — see finalTotal);
  //  - otherwise fall back to points × rate, capped at the running total so
  //    it can never exceed what's owed.
  // Cap the estimate at the item subtotal so it can never look larger than
  // what's being bought (the backend enforces the real max on redemption).
  const estimatedLoyaltyDiscount = Math.min(
    enteredRedeemPoints * redemptionRate,
    Number.isFinite(analyzedSubtotal) && analyzedSubtotal > 0 ? analyzedSubtotal : calculateBillSummaryTotal
  );
  const loyaltyDiscountFromBackend = analyzedLoyaltyDiscount > 0;
  const loyaltyDiscountToShow = loyaltyDiscountFromBackend
    ? analyzedLoyaltyDiscount
    : estimatedLoyaltyDiscount;
  const analyzedDeliveryFee = (() => {
    // ShipRocket rate takes priority when serviceable
    if (shippingInfo?.serviceable === true && shippingInfo?.rate != null) {
      return Number(shippingInfo.rate);
    }
    return Number(
      analyzedCartStatus?.delivery_charge ??
      analyzedCartStatus?.delivery_fee ??
      analyzedCartStatus?.shipping_charge ??
      cartAnalysis?.delivery_charge ??
      cartAnalysis?.delivery_fee ??
      cartAnalysis?.shipping_charge
    );
  })();
  // ✅ FIXED: Manual distance calculation is commented out.
  // Since we rely entirely on the API (Shiprocket or backend defaults),
  // calculating distance is inefficient and irrelevant for national shipping.
  /*
  const fallbackDeliveryFee = React.useMemo(() => {
    if (orderType === 'collection') return 0;
    const basicCharge = settingsService.getBasicDeliveryCharge();
    const targetAddress = selectedAddress || defaultAddress;

    if (selectedStore?.latitude && selectedStore?.longitude &&
        targetAddress?.latitude && targetAddress?.longitude) {
      const dist = calculateDistance(
        parseFloat(selectedStore.latitude),
        parseFloat(selectedStore.longitude),
        parseFloat(targetAddress.latitude),
        parseFloat(targetAddress.longitude)
      );
      const freeKm = settingsService.getFreeDeliveryKilometer();
      if (dist <= freeKm) return 0;
      const perKm = settingsService.getChargePerKilo();
      return basicCharge + ((dist - freeKm) * perKm);
    }
    return basicCharge;
  }, [orderType, selectedStore, selectedAddress, defaultAddress]);
  */

  // If the API returns a rate (even 0), use it. Otherwise, assume 0.
  const deliveryFee = Number.isFinite(analyzedDeliveryFee)
    ? analyzedDeliveryFee
    : 0; // Fallback to 0 instead of manual calculation

  // ✅ LOCAL TAX CALCULATION — uses item tax_group → category tax fallback
  const localTaxResult = React.useMemo(() => {
    return calculateCartTaxes(cart, categories, calculateItemPrice);
  }, [cart, categories]);

  const taxBreakdown = React.useMemo(() => {
    // If backend returned a non-empty tax_breakdown, use it
    if (Array.isArray(analyzedCartStatus?.tax_breakdown) && analyzedCartStatus.tax_breakdown.length > 0) {
      return analyzedCartStatus.tax_breakdown;
    }
    // Otherwise fallback to locally computed tax breakdown
    return localTaxResult.tax_breakdown;
  }, [analyzedCartStatus?.tax_breakdown, localTaxResult.tax_breakdown]);

  const effectiveTaxTotal = React.useMemo(() => {
    if (taxBreakdown.length > 0) {
      return taxBreakdown.reduce((sum, tax) => {
        return sum + Number(tax?.tax_amount || 0);
      }, 0);
    }
    if (Number.isFinite(analyzedTaxTotal) && analyzedTaxTotal > 0) {
      return analyzedTaxTotal;
    }
    return localTaxResult.tax_total;
  }, [taxBreakdown, analyzedTaxTotal, localTaxResult.tax_total]);

  const billSummaryTotal = Number.isFinite(analyzedSubtotal)
    ? analyzedSubtotal
    : calculateBillSummaryTotal;

  // Track if we are in a "stale" state while analyzing
  const showLoadingState = isAnalyzing;

  // Keep "To Pay" consistent with Bill Summary rows and prevent missed discount deduction.
  const finalTotal = React.useMemo(() => {
    const deliveryCharge = orderType === 'collection' || CHECKOUT_FLOW_CONFIG.USE_COD_ONLY_FLOW ? 0 : deliveryFee;
    const totalDiscounts = Number(parseFloat(getTotalSavings) || 0);
    const freeItemsValue = Number(parseFloat(getFreeItemsValue) || 0);
    const localOfferSavings = Math.max(0, totalDiscounts + freeItemsValue);

    // Loyalty: only subtract here when the backend HASN'T already done it.
    // If /web/cart/analyze returned a loyalty discount, analyzedGrandTotal is
    // already net of it; otherwise apply the points × rate estimate.
    const loyaltyToApply = loyaltyDiscountFromBackend ? 0 : estimatedLoyaltyDiscount;

    if (Number.isFinite(analyzedGrandTotal)) {
      const backendTax = Number.isFinite(analyzedTaxTotal) ? analyzedTaxTotal : 0;
      const uncountedTax = Math.max(0, effectiveTaxTotal - backendTax);
      const analyzedTotalWithDeliveryAndTax = Math.max(0, analyzedGrandTotal + deliveryCharge + uncountedTax);
      const rawNoDiscountTotal = calculateBillSummaryTotal + effectiveTaxTotal + deliveryCharge;
      const savingsAlreadyAppliedByApi = Math.max(0, rawNoDiscountTotal - analyzedTotalWithDeliveryAndTax);
      const remainingSavingsToApply = Math.max(0, localOfferSavings - savingsAlreadyAppliedByApi);

      return Math.max(0, analyzedTotalWithDeliveryAndTax - remainingSavingsToApply - loyaltyToApply);
    }

    const rowTotalBeforeSavings = billSummaryTotal + effectiveTaxTotal + deliveryCharge;
    return Math.max(0, rowTotalBeforeSavings - localOfferSavings - loyaltyToApply);
  }, [
    analyzedGrandTotal,
    billSummaryTotal,
    calculateBillSummaryTotal,
    deliveryFee,
    effectiveTaxTotal,
    getFreeItemsValue,
    getTotalSavings,
    orderType,
    loyaltyDiscountFromBackend,
    estimatedLoyaltyDiscount
  ]);

  // ============================================
  // DELIVERY RADIUS CHECK (must be before early return to satisfy hooks rules)
  // Check if the selected address is within the store's delivery radius
  // If out of range, show warning and block ordering
  // ============================================
  const displayAddress = selectedAddress || defaultAddress;

  const deliveryRadiusCheck = React.useMemo(() => {
    if (orderType !== 'delivery') return { isOutOfRadius: false, distance: 0, maxRadius: 0 };
    if (!displayAddress?.latitude || !displayAddress?.longitude) return { isOutOfRadius: false, distance: 0, maxRadius: 0 };
    if (!selectedStore?.latitude || !selectedStore?.longitude) return { isOutOfRadius: false, distance: 0, maxRadius: 0 };

    const distance = calculateDistance(
      parseFloat(selectedStore.latitude),
      parseFloat(selectedStore.longitude),
      parseFloat(displayAddress.latitude),
      parseFloat(displayAddress.longitude)
    );

    // Use store's delivery_radius, fallback to 50km
    const maxRadius = parseFloat(selectedStore.delivery_radius) || 50;
    const isOutOfRadius = false; // Application available all over the country

    return { isOutOfRadius, distance: Math.round(distance * 10) / 10, maxRadius };
  }, [orderType, displayAddress, selectedStore]);

  const { isOutOfRadius, distance: addressDistance, maxRadius: storeMaxRadius } = deliveryRadiusCheck;

  // ShipRocket: block order if delivery is explicitly not serviceable to this address
  const isShiprocketNotServiceable = orderType === 'delivery' &&
    shippingInfo !== null &&
    shippingInfo?.serviceable === false;

  if (cart.length === 0) {
    return (
      <div className="menu-app-cart-sidebar-empty">
        <div className="menu-app-cart-empty-icon-wrapper">
          <ShoppingBag size={56} className="menu-app-cart-empty-icon" />
        </div>
        <h3 className="menu-app-cart-empty-title">Your Cart is Empty</h3>
        <p className="menu-app-cart-empty-subtitle">
          Looks like you haven't added anything yet.<br />
          Explore our menu to find your favorites!
        </p>
      </div>
    );
  }

  const appliedOffers = offersManager?.getAllAppliedOffers() || [];
  const totalSavings = parseFloat(getTotalSavings);

  return (
    <div className="cart-content-container">
      {/* SAVINGS BANNER */}
      {totalSavings > 0 && (
        <div className="cart-savings-banner">
          <div className="savings-banner-content">
            <span className="savings-banner-text">
              Yay! You <strong>saved {settingsService.getCurrencySymbol()}{totalSavings.toFixed(0)}</strong> on this order
            </span>
          </div>
          <ChevronDown size={16} className="savings-banner-icon" />
        </div>
      )}

      {/* SCROLLABLE CONTENT */}
      <div className="cart-scrollable-content">
        <div className="cart-scrollable-content-scroll" ref={cartScrollRef}>
          <div className="menu-app-cart-items">

            {cart.map(item => {
              const canEdit = hasCustomizations(item);

              const itemPrice = calculateItemPrice(item);
              const itemTotal = itemPrice * item.quantity;

              return (
                <div key={item.cartItemKey || item.id} className="menu-app-cart-item">
                  <div className="menu-app-cart-item-details">
                    <div className="cart-item-name-row">
                      <h3 className="menu-app-cart-item-name">{item.name}</h3>
                      {item.spiceLevel > 0 && (
                        <span style={{ marginLeft: 6 }}><SpiceLevel level={item.spiceLevel} size={11} /></span>
                      )}
                      {canEdit && !item.isCombo && ( /* Don't show edit for combos */
                        <button
                          type="button"
                          className="cart-item-edit-btn-inline"
                          onClick={() => onEditCustomization(item)}
                          aria-label="Edit item"
                        >
                          <Edit2 size={13} />
                        </button>
                      )}
                    </div>

                    {/* Show combo items BEFORE customizations */}
                    {item.isCombo && (item.comboItems || item.comboComponents || item.combo_components) && (
                      <div className="combo-items-list">
                        {(item.comboItems || item.comboComponents || item.combo_components || []).map((comboItem, idx) => (
                          <p key={idx} className="combo-item-detail">
                            {comboItem.component_item_name || comboItem.item_name || comboItem.name}
                            {(comboItem.component_variation_name || comboItem.variation_name) && ` (${comboItem.component_variation_name || comboItem.variation_name})`}
                            {(comboItem.quantity > 1 || parseFloat(comboItem.quantity) > 1) && ` ×${parseFloat(comboItem.quantity)}`}
                          </p>
                        ))}
                      </div>
                    )}

                    {/* Regular customizations for non-combo items */}
                    {item.variations && item.variations.length > 0 && (
                      <p className="menu-app-cart-item-customization">
                        {!item.isCombo && <span className="customization-label">Size:</span>} {item.variations.map(v => v.name || v.name).join(', ')}
                      </p>
                    )}

                    {/* ✅ Weight Based Items: Display selected weight */}
                    {item.isWeightBased && item.weightLabel && (
                      <p className="menu-app-cart-item-customization weight-label-display">
                        <span className="customization-label">Weight:</span> {item.weightLabel}
                      </p>
                    )}

                    {item.extras && item.extras.length > 0 && (
                      <p className="menu-app-cart-item-customization">
                        {!item.isCombo && <span className="customization-label">Extras:</span>} {item.extras.map(e => e.extra_name || e.name).join(', ')}
                      </p>
                    )}

                    {item.addons && item.addons.length > 0 && (
                      <p className="menu-app-cart-item-customization">
                        {!item.isCombo && <span className="customization-label">Add-on:</span>} {item.addons.map(a => a.addon_item_name).join(', ')}
                      </p>
                    )}

                    {/* {!item.isCombo && !item.isWeightBased && item.unit && (
                      <p className="menu-app-cart-item-customization">
                        <span className="customization-label">Unit:</span> {item.unit === 'pc' ? 'Piece' : item.unit}
                      </p>
                    )} */}
                  </div>

                  <div className="cart-item-footer">
                    {/* Delete first, then the quantity stepper, then the price —
                        matches the POS cart row layout. */}
                    <button
                      onClick={() => onRemove(item.cartItemKey)}
                      className="menu-app-cart-item-remove-inline"
                      title="Remove item"
                    >
                      <Trash2 size={15} />
                    </button>

                    <div className="menu-app-cart-item-controls">
                      <button
                        onClick={() => onUpdateQuantity(item.cartItemKey, -1)}
                        className="menu-app-qty-btn minus"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="menu-app-qty-display">{item.quantity}</span>
                      <button
                        onClick={() => onUpdateQuantity(item.cartItemKey, 1)}
                        className="menu-app-qty-btn plus"
                      >
                        <Plus size={14} />
                      </button>
                    </div>

                    <div className="menu-app-cart-item-amount">
                      <div className="cart-item-total">{formatPrice(itemTotal)}</div>
                      {item.quantity > 1 && (
                        <div className="cart-item-breakdown" style={{ fontSize: '11px', color: '#6b7280', textAlign: 'right' }}>
                          {item.quantity} × {formatPrice(itemPrice)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* FREE ITEMS */}
          {offersManager?.getAllAppliedOffers().some(offer =>
            offer.free_items && offer.free_items.length > 0
          ) && (
              <div className="free-items-section-compact">
                <div className="free-items-header-compact">
                  <Gift size={16} color="#10b981" />
                  <span className="free-items-title-compact">Free Items</span>
                </div>
                {offersManager.getAllAppliedOffers()
                  .filter(offer => offer.free_items && offer.free_items.length > 0)
                  .map((offer, offerIndex) =>
                    offer.free_items.map((freeItem, itemIndex) => (
                      <div
                        key={`free-${offerIndex}-${itemIndex}`}
                        className="free-item-compact"
                      >
                        <FallbackImg
                          src={freeItem.image}
                          alt={freeItem.name}
                          className="free-item-image-compact"
                        />

                        <div className="free-item-info-compact">
                          <div className="free-item-name-row">
                            <span className="free-item-name-compact">{freeItem.name}</span>
                            <span className="free-item-badge-compact">
                              <Gift size={10} />
                              FREE
                            </span>
                          </div>
                          <span className="free-item-offer-compact">From: {offer.name}</span>
                        </div>

                        <span className="free-item-qty-compact">×{freeItem.quantity || 1}</span>
                      </div>
                    ))
                  )
                }
              </div>
            )}

          {/* RECOMMENDATIONS */}
          {/* {recommendations && recommendations.length > 0 && (
          <div className="recommendations-section-compact">
            <div className="recommendations-header-compact">
              <TrendingUp size={16} color="#f97316" />
              <span className="recommendations-title-compact">Recommendations</span>
            </div>
            {recommendations.map((rec, index) => {
              const color = getRecommendationColor(rec.type);

              return (
                <div
                  key={index}
                  className="recommendation-compact"
                  style={{
                    background: `${color}08`,
                    border: `1.5px solid ${color}30`
                  }}
                >
                  <div
                    className="recommendation-icon-compact"
                    style={{
                      backgroundColor: `${color}20`,
                      color: color
                    }}
                  >
                    {getRecommendationIcon(rec.type)}
                  </div>

                  <div className="recommendation-info-compact">
                    <div className="recommendation-message-row">
                      <span className="recommendation-text-compact">{rec.message}</span>
                      {rec.potential_savings && (
                        <span
                          className="recommendation-badge-compact"
                          style={{
                            backgroundColor: '#10b981',
                            color: 'white'
                          }}
                        >
                          Save {formatCurrency(rec.potential_savings)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div
                    className="recommendation-arrow-compact"
                    style={{ color: color }}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 20 20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <path d="M7 14l5-5-5-5" />
                    </svg>
                  </div>
                </div>
              );
            })}
          </div>
        )} */}

          {/* ========================================
            SCHEDULE DELIVERY/COLLECTION
            Implements complete scenario matrix
            ======================================== */}
          {showScheduleUI && selectedStore?.week_schedule && (
            <div className="collection-time-section">
              {/* Header */}
              <div className="schedule-section-header">
                <Clock size={18} />
                <span>
                  {orderType === 'collection' ? 'Pickup Time' : 'Delivery Time'}
                </span>


                {/* ========================================
                SCENARIO 1: Store OPEN + Pre-Order ENABLED + DELIVERY
                Show: ASAP vs Schedule Toggle
                ======================================== */}
                {canShowScheduleToggle && (
                  <div className="schedule-dropdown-wrapper">
                    <select
                      className="schedule-dropdown"
                      value={scheduleMode}
                      onChange={(e) => {
                        const mode = e.target.value;
                        setScheduleMode(mode);
                        if (mode === 'asap') {
                          setShowTimePicker(false);
                          const asapTime = getASAPDeliveryTime();
                          onTimeChange(asapTime);
                        } else {
                          setShowTimePicker(true);
                          onTimeChange(null);
                        }
                      }}
                    >
                      <option value="asap">ASAP</option>
                      <option value="later">Schedule for Later</option>
                    </select>
                    <ChevronDown size={14} className="dropdown-icon" />
                  </div>
                )}
              </div>


              {/* ========================================
                SCENARIO 2 & 3: ASAP Display
                Show when: Store OPEN + NOT pre-order + (Delivery OR Collection)
                ======================================== */}
              {scheduleMode === 'asap' && canShowASAP && !canShowScheduleToggle && (orderType === 'collection' || hasDeliveryAddress) && (
                <div className="asap-delivery-info">
                  <div className="asap-delivery-icon">
                    <Zap size={20} />
                  </div>
                  <div className="asap-delivery-details">
                    <strong>
                      {orderType === 'collection' ? 'Ready' : 'Delivery'}
                    </strong>
                    <span>
                      {orderType === 'collection'
                        ? 'Ready in 15-20 minutes'
                        : CHECKOUT_FLOW_CONFIG.USE_COD_ONLY_FLOW
                          ? 'Contact Us'
                          : `Expected delivery: ${getASAPDisplayTime()}`
                      }
                    </span>
                  </div>
                </div>
              )}

              {/* ASAP Display with Toggle (Pre-Order Delivery) */}
              {scheduleMode === 'asap' && canShowScheduleToggle && hasDeliveryAddress && (
                <div className="asap-delivery-info">
                  <div className="asap-delivery-icon">
                    <Zap size={20} />
                  </div>
                  <div className="asap-delivery-details">
                    <strong>Delivery</strong>
                    <span>
                      {CHECKOUT_FLOW_CONFIG.USE_COD_ONLY_FLOW
                        ? 'Contact Us'
                        : `Expected delivery: ${getASAPDisplayTime()}`
                      }
                    </span>
                  </div>
                </div>
              )}

              {/* ========================================
                SCENARIOS: Time Picker Display
                Show when user needs to select a time slot
                ======================================== */}
              {(
                // User chose "Schedule for Later" (pre-order delivery)
                (canShowScheduleToggle && scheduleMode === 'later') ||

                // Store OPEN + Pre-Order ENABLED + COLLECTION (always show picker)
                (storeIsOpen && isPreOrder && orderType === 'collection') ||

                // Store OPEN + Pre-Order DISABLED + COLLECTION (today only)
                (storeIsOpen && !isPreOrder && orderType === 'collection') ||

                // Store CLOSED + Pre-Order supported (must schedule)
                (!storeIsOpen && storeSupportsPreOrder)
              ) && (
                  <div className="collection-time-picker-wrapper">
                    {/* Store Closed Banner */}
                    {!storeIsOpen && (
                      <div className="pre-order-schedule-banner">
                        <div className="pre-order-schedule-icon">
                          <Calendar size={18} />
                        </div>
                        <div className="pre-order-schedule-text">
                          <strong>Store Currently Closed</strong>
                          <span>
                            Select a future {orderType === 'collection' ? 'pickup' : 'delivery'} time
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Store Open - Schedule for Later (Pre-Order Delivery) */}


                    {/* Store Open - Collection (Today Only) */}


                    {/* Store Open - Collection (Pre-Order) */}
                    {storeIsOpen && isPreOrder && orderType === 'collection' && (
                      <div className="schedule-open-banner">
                        <div className="schedule-open-icon">
                          <Calendar size={18} />
                        </div>
                        <div className="schedule-open-text">
                          <strong>Schedule Pickup Time</strong>
                          <span>Choose any future pickup slot</span>
                        </div>
                      </div>
                    )}

                    {/* Pre-selected Date Info */}
                    {isPreOrder && scheduleInfo && orderType === 'delivery' && (
                      <div className="pre-selected-date-info">
                        <Calendar size={16} />
                        <span>Pre-selected: {scheduleInfo.displayDate}</span>
                      </div>
                    )}

                    {/* Time Picker Component */}
                    <TimePicker
                      weekSchedule={selectedStore.week_schedule}
                      selectedDateTime={selectedTime}
                      onSelectDateTime={(dateTime) => {
                        console.log('📅 Time selected:', dateTime);
                        onTimeChange(dateTime);
                      }}
                      className="cart-time-picker"
                      isPreOrder={isPreOrder || (!storeIsOpen && storeSupportsPreOrder) || (storeSupportsPreOrder && scheduleMode === 'later')}
                      preSelectedDate={scheduleInfo?.date || null}
                      orderType={orderType}
                    />
                  </div>
                )}

              {/* ✅ Gap B fix: Store CLOSED + Pre-Order NOT supported — no valid
                future time exists to schedule against, so show a plain
                closed/unavailable state instead of the time picker. Mirrors
                the empty-state pattern TimePicker itself already uses. */}
              {!storeIsOpen && !storeSupportsPreOrder && (
                <div className="collection-time-picker-wrapper">
                  <div className="pre-order-schedule-banner">
                    <div className="pre-order-schedule-icon">
                      <Calendar size={18} />
                    </div>
                    <div className="pre-order-schedule-text">
                      <strong>Store Currently Closed</strong>
                      <span>Pre-orders are not available at this location</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Cooking Instruction Input — hidden for outlet types with no
            kitchen to instruct (apparel, grocery, salon) */}
          {INSTRUCTIONS_PLACEHOLDER_BY_OUTLET_TYPE[outletType] && (
            <div className="cart-instruction-section">
              <div className="cart-instruction-header">
                <Edit2 size={16} />
                <span>Instructions (Optional)</span>
              </div>
              <textarea
                className="cart-instruction-input"
                placeholder={INSTRUCTIONS_PLACEHOLDER_BY_OUTLET_TYPE[outletType]}
                value={cookingInstruction}
                onChange={(e) => setCookingInstruction(e.target.value)}
                rows={2}
              />
            </div>
          )}

          {/* Bill Summary */}
          <div className="bill-summary-container">
            <div className="bill-summary-header">
              <span>Bill Summary</span>
            </div>

            <div className="bill-summary-rows">
              <div className="bill-row">
                <span className="bill-label">Item Total</span>
                <span className="bill-value">{formatPrice(billSummaryTotal)}</span>

              </div>

              {appliedOffers && appliedOffers.length > 0 && (
                <>
                  {appliedOffers
                    .filter(offer => offer.discount_amount > 0)
                    .map((offer, index) => {
                      console.log(` Discount: ${offer.name} = ${settingsService.getCurrencySymbol()}${offer.discount_amount}`);
                      return (
                        <div key={offer.id || index} className="bill-row discount-row">
                          <div className="discount-info">
                            <Percent size={14} />
                            <span>{offer.name}</span>
                          </div>
                          <span className="bill-value discount">
                            -{formatPrice(offer.discount_amount || 0)}
                          </span>
                        </div>
                      );
                    })
                  }
                  {/*  Show total discount if individual offers don't show discount */}
                  {appliedOffers.filter(o => o.discount_amount > 0).length === 0 && totalSavings > 0 && (
                    <div className="bill-row discount-row">
                      <div className="discount-info">
                        <Percent size={14} />
                        <span>Discount</span>
                      </div>
                      <span className="bill-value discount">
                        -{formatPrice(totalSavings)}
                      </span>
                    </div>
                  )}
                </>
              )}

              {(offersManager?.getFreeItemsValue() || 0) > 0 && (
                <div className="bill-row info-row-compact">
                  <div className="info-content-compact">
                    <Gift size={14} />
                    <span>Free Items</span>
                  </div>
                  <span className="bill-value saved-compact">
                    {formatPrice(offersManager?.getFreeItemsValue() || 0)} saved
                  </span>
                </div>
              )}

              {taxBreakdown.length > 0 ? (
                taxBreakdown.map((tax, index) => {
                  const taxName = tax?.tax_name || `Tax ${index + 1}`;
                  const rawTaxRate = Number(tax?.tax_rate);
                  const taxRateLabel = Number.isFinite(rawTaxRate) ? ` (${rawTaxRate.toFixed(2)}%)` : '';
                  const taxAmount = Number(tax?.tax_amount || 0);

                  return (
                    <div className="bill-row" key={`${taxName}-${index}`}>
                      <span className="bill-label">{`${taxName}${taxRateLabel}`}</span>
                      <span className="bill-value">{formatPrice(taxAmount)}</span>
                    </div>
                  );
                })
              ) : Number.isFinite(analyzedTaxTotal) && analyzedTaxTotal > 0 ? (
                <div className="bill-row">
                  <span className="bill-label">Tax</span>
                  <span className="bill-value">{formatPrice(analyzedTaxTotal)}</span>
                </div>
              ) : null}

              {orderType === 'delivery' && shippingInfo !== null && shippingInfo?.serviceable === false ? (
                <div className="bill-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <span className="bill-label">Delivery Fee</span>
                    <span className="bill-value" style={{ color: '#ef4444' }}>—</span>
                  </div>
                  <span style={{ fontSize: '1.3rem', color: '#ef4444' }}>
                    {shippingInfo?.message || "Delivery not available to this address"}
                  </span>
                </div>
              ) : orderType !== 'collection' ? (
                <div className="bill-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <span className="bill-label">Delivery Fee</span>
                    <span className={`bill-value ${showLoadingState ? 'analyzing-fade' : ''}`}>
                      {showLoadingState ? (
                        <span className="analysis-shimmer">Calculating...</span>
                      ) : CHECKOUT_FLOW_CONFIG.USE_COD_ONLY_FLOW
                        ? <span style={{ fontSize: '0.85em', color: '#16a34a' }}>Contact Us</span>
                        : formatPrice(deliveryFee)
                      }
                    </span>
                  </div>
                  {!showLoadingState && shippingInfo?.serviceable === true && shippingInfo?.courier_name && (
                    <span className="bill-delivery-courier-note">
                      via {shippingInfo.courier_name}
                      {shippingInfo.estimated_days ? ` · Est. ${shippingInfo.estimated_days} day${shippingInfo.estimated_days > 1 ? 's' : ''}` : ''}
                    </span>
                  )}
                  {/* Air vs Surface — shown when both are offered for this address. Each has its own
                      fee and delivery period; the highlighted one is the fee charged above. */}
                  {!showLoadingState && !CHECKOUT_FLOW_CONFIG.USE_COD_ONLY_FLOW && shippingInfo?.serviceable === true &&
                    shippingInfo?.options?.air && shippingInfo?.options?.surface && (
                      <div style={{ display: 'flex', gap: '0.8rem', width: '100%', marginTop: '0.6rem' }}>
                        {[['air', 'Air'], ['surface', 'Surface']].map(([key, label]) => {
                          const opt = shippingInfo.options[key];
                          const selected = shippingInfo.selected_speed === key;
                          const days = Number(opt.estimated_days) || 0;
                          return (
                            <button
                              key={key}
                              type="button"
                              disabled={isAnalyzing}
                              onClick={() => { if (!selected) onDeliverySpeedChange(key); }}
                              aria-pressed={selected}
                              style={{
                                flex: 1,
                                textAlign: 'left',
                                padding: '0.8rem 1rem',
                                borderRadius: '0.8rem',
                                border: selected ? '2px solid #16a34a' : '1px solid #d1d5db',
                                background: selected ? '#f0fdf4' : '#fff',
                                cursor: isAnalyzing ? 'default' : 'pointer',
                                opacity: isAnalyzing ? 0.7 : 1,
                              }}
                            >
                              <div style={{ fontSize: '1.3rem', fontWeight: 600, color: '#111827' }}>{label}</div>
                              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>{formatPrice(Number(opt.rate))}</div>
                              <div style={{ fontSize: '1.2rem', color: '#6b7280' }}>
                                {days > 0 ? `${days} day${days > 1 ? 's' : ''}` : (opt.etd ? `By ${opt.etd}` : 'Delivery time n/a')}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                </div>
              ) : null}

              {/* ✅ Redeem Points — only for a logged-in customer with a
                  positive available balance. Label mirrors the POS:
                  "Redeem Points (257 available, min 20)". The discount itself
                  is computed by the backend at order creation; the amount
                  shown here is that value when /web/cart/analyze returns it,
                  otherwise a points × rate estimate. */}
              {userId && availablePoints > 0 && (
                <div className="bill-row redeem-points-row">
                  <div className="redeem-points-top">
                    <div className="discount-info">
                      <Gift size={14} />
                      <span>
                        Redeem Points ({availablePoints} available, min {minRedeemPoints})
                      </span>
                    </div>
                    <input
                      type="number"
                      inputMode="numeric"
                      className="redeem-points-input"
                      placeholder="Points"
                      min={0}
                      max={availablePoints}
                      value={redeemInput}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') {
                          setRedeemInput('');
                          onRedeemPointsChange(0);
                          return;
                        }
                        let val = parseInt(raw, 10);
                        if (isNaN(val) || val < 0) val = 0;
                        if (val > availablePoints) val = availablePoints;
                        setRedeemInput(String(val));
                        // Only apply (send up / reduce total) once it clears
                        // the minimum — below that it can't actually redeem.
                        onRedeemPointsChange(val >= minRedeemPoints ? val : 0);
                      }}
                    />
                  </div>

                  {typedRedeemPoints > 0 && (
                    <div className="redeem-points-applied">
                      {redeemBelowMinimum ? (
                        <span className="redeem-points-note">Minimum {minRedeemPoints} points to redeem</span>
                      ) : loyaltyDiscountToShow > 0 ? (
                        <span className="redeem-points-discount">
                          − {formatPrice(loyaltyDiscountToShow)}
                          {!loyaltyDiscountFromBackend && <span className="redeem-points-note"> (est.)</span>}
                        </span>
                      ) : showLoadingState ? (
                        <span className="redeem-points-note">Applying…</span>
                      ) : (
                        <span className="redeem-points-note">{typedRedeemPoints} pts</span>
                      )}
                      <button
                        type="button"
                        className="redeem-points-clear"
                        onClick={() => {
                          setRedeemInput('');
                          onRedeemPointsChange(0);
                        }}
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="bill-total-row">
              <span className="total-label">To Pay</span>
              <span className={`final-total ${showLoadingState ? 'analyzing-fade' : ''}`}>
                {showLoadingState ? '...' : formatPrice(finalTotal)}
              </span>
            </div>
          </div>
        </div>
        <div className="cart-scrollable-content-scrollbar-track" ref={cartTrackRef} aria-hidden="true">
          <div
            className="cart-scrollable-content-scrollbar-thumb"
            ref={cartThumbRef}
            onPointerDown={handleCartThumbPointerDown}
          />
        </div>
      </div>

      {/* FIXED BOTTOM SECTION */}
      <div className="fixed-bottom-section">

        {orderType === "delivery" && (
          <>
            <div
              className={`zepto-address-compact ${isOutOfRadius ? 'out-of-radius' : ''}`}
              onClick={() => setShowAddressModal(true)}
            >
              <div className={`zepto-address-icon ${isOutOfRadius ? 'error' : ''}`}>
                {isOutOfRadius ? <MapPinOff size={18} /> : <MapPin size={18} />}
              </div>
              <div className="zepto-address-content">
                {loadingAddresses ? (
                  <>
                    <span className="zepto-address-label">Loading...</span>
                    <span className="zepto-address-text">Fetching your addresses</span>
                  </>
                ) : addressError ? (
                  <>
                    <span className="zepto-address-label">No Address</span>
                    <span className="zepto-address-text">Click to add delivery address</span>
                  </>
                ) : selectedAddress ? (
                  <>
                    <span className="zepto-address-label">{selectedAddress.label}</span>
                    <span className="zepto-address-text">
                      {selectedAddress.address}
                      {selectedAddress.apartment && `, ${selectedAddress.apartment}`}
                      {selectedAddress.city && `, ${selectedAddress.city}`}
                      {selectedAddress.state && `, ${selectedAddress.state}`}
                      {(selectedAddress.post_code || selectedAddress.zip_code) && ` - ${selectedAddress.post_code || selectedAddress.zip_code}`}
                    </span>
                  </>
                ) : hasAddresses ? (
                  <>
                    <span className="zepto-address-label">Select Address</span>
                    <span className="zepto-address-text">Click to choose delivery address</span>
                  </>
                ) : (
                  <>
                    <span className="zepto-address-label">No Address</span>
                    <span className="zepto-address-text">Click to add delivery address</span>
                  </>
                )}
              </div>
              <ChevronDown size={20} className="zepto-address-chevron" />
            </div>

            {/* Out of delivery radius warning */}
            {isOutOfRadius && displayAddress && (
              <div className="cart-undeliverable-banner">
                <div className="cart-undeliverable-icon">
                  <MapPinOff size={18} />
                </div>
                <div className="cart-undeliverable-content">
                  <span className="cart-undeliverable-title">
                    Cannot deliver to this location
                  </span>
                  <span className="cart-undeliverable-text">
                    This address is {addressDistance} km away. We deliver within {storeMaxRadius} km.
                    Please select a closer address.
                  </span>
                </div>
              </div>
            )}
          </>
        )}

        <div className="fixed-order-button-container">
          {/* Time Selection Warning - Only for non-ASAP orders */}
          {!selectedTime && scheduleMode !== 'asap' && (
            <div className="pre-order-time-warning">
              <AlertCircle size={16} />
              <span>
                {`Please select a ${orderType === 'collection' ? 'pickup' : 'delivery'} time`}
              </span>
            </div>
          )}

          {/* Address Warning for Delivery */}
          {orderType === 'delivery' && !displayAddress && !loadingAddresses && (
            <div className="pre-order-time-warning">
              <AlertCircle size={16} />
              <span>Please add a delivery address</span>
            </div>
          )}

          <button
            onClick={() => {
              if (orderType === 'delivery' && !displayAddress) {
                setShowAddressModal(true);
                return;
              }
              if (orderType === 'collection' && !selectedTime) {
                alert('Please select a pickup time above');
                const timeOptionRow = document.querySelector('.time-selection-container');
                if (timeOptionRow) timeOptionRow.scrollIntoView({ behavior: 'smooth' });
                return;
              }
              onPlaceOrder();
            }}
            disabled={
              loading ||
              isAnalyzing ||
              //  CRITICAL: When "Schedule for Later" selected, MUST have time
              (scheduleMode === 'later' && !selectedTime) ||
              //  DELIVERY RADIUS: Block if address is out of range
              (orderType === 'delivery' && isOutOfRadius) ||
              //  SHIPROCKET: Block if not serviceable to this address
              isShiprocketNotServiceable ||
              //  Gap B fix: Store closed with no pre-order support — no valid
              //  order can be placed at all, scheduled or otherwise
              (!storeIsOpen && !storeSupportsPreOrder)
            }
            className={`fixed-order-button ${scheduleMode === 'asap' ? 'asap-mode' : ''} ${(isOutOfRadius || isShiprocketNotServiceable) && orderType === 'delivery' ? 'out-of-radius' : ''}`}
            title={
              (!storeIsOpen && !storeSupportsPreOrder)
                ? 'Store is currently closed'
                : isShiprocketNotServiceable
                  ? 'Delivery not available to this address'
                  : isOutOfRadius && orderType === 'delivery'
                    ? 'Selected address is outside delivery area'
                    : (orderType === 'collection' && !selectedTime)
                      ? 'Please select a pickup time'
                      : (!displayAddress && orderType === 'delivery'
                        ? 'Please add a delivery address'
                        : '')
            }
          >
            {loading
              ? 'Placing Order...'
              : isAnalyzing
                ? 'CALCULATING...'
                : (!storeIsOpen && !storeSupportsPreOrder)
                  ? 'STORE CLOSED'
                  : isShiprocketNotServiceable
                    ? 'CANNOT DELIVER HERE'
                    : isOutOfRadius && orderType === 'delivery'
                      ? 'CANNOT DELIVER HERE'
                      : scheduleMode === 'asap'
                        ? 'ORDER NOW'
                        : !storeIsOpen || isPreOrder
                          ? 'SCHEDULE ORDER'
                          : 'ORDER NOW'
            }
          </button>
        </div>
      </div>

      {showAddressModal && (
        <AddressModal
          isOpen={showAddressModal}
          onClose={() => setShowAddressModal(false)}
          addresses={addresses}
          selectedAddress={selectedAddress}
          selectedStore={selectedStore}
          onSelectAddress={(addr) => {
            onAddressChange(addr);
            setShowAddressModal(false);
          }}
          onCreateAddress={createAddress}
          onUpdateAddress={updateAddress}
          onDeleteAddress={deleteAddress}
          onSetDefaultAddress={setDefaultAddress}
          loading={loadingAddresses}
          operationInProgress={operationInProgress}
        />
      )}
    </div>
  );
};

//  FIXED: Memoize CartContent to prevent re-render loops
// Only re-render if cart items or critical props actually change
export default React.memo(CartContent, (prevProps, nextProps) => {
  // Return TRUE if props are equal (skip re-render)
  // Return FALSE if props changed (do re-render)

  // Check if cart array reference changed
  if (prevProps.cart !== nextProps.cart) {
    return false; // Cart changed, DO re-render
  }

  // Check if selected time changed (CRITICAL for Collection orders)
  if (prevProps.selectedTime !== nextProps.selectedTime) {
    return false; // Time changed, DO re-render
  }

  // FIXED: Check if offers changed (totalSavings is key indicator)
  if (prevProps.getTotalSavings !== nextProps.getTotalSavings) {
    return false; // Offers changed, DO re-render
  }
  if (prevProps.getFreeItemsValue !== nextProps.getFreeItemsValue) {
    return false; // Free items value changed, DO re-render
  }

  // Check if order type changed
  if (prevProps.orderType !== nextProps.orderType) {
    return false; // Order type changed, DO re-render
  }

  // Check if cart analysis changed
  if (prevProps.cartAnalysis !== nextProps.cartAnalysis) {
    return false; // Cart analysis changed, DO re-render
  }

  if (prevProps.isAnalyzing !== nextProps.isAnalyzing) {
    return false; // Analysis state changed, DO re-render
  }

  // Check if selected address changed
  if (prevProps.selectedAddress !== nextProps.selectedAddress) {
    return false; // Address changed, DO re-render
  }

  // Check if store open status changed
  if (prevProps.storeIsOpen !== nextProps.storeIsOpen) {
    return false; // Store status changed, DO re-render
  }

  // Check if cooking instruction changed
  if (prevProps.cookingInstruction !== nextProps.cookingInstruction) {
    return false; // Instruction changed, DO re-render
  }

  // ✅ Redeem Points input is controlled from the parent (same pattern as
  // cookingInstruction above) — without this check, typing into it updates
  // RestaurantMenuApp's state but this memoized component never re-renders
  // to show it, so the input silently "won't take" keystrokes.
  if (prevProps.redeemPoints !== nextProps.redeemPoints) {
    return false; // Redeemed points changed, DO re-render
  }
  if (prevProps.loyaltyPoints !== nextProps.loyaltyPoints) {
    return false; // Available loyalty points changed, DO re-render
  }

  // All other props are the same, skip re-render
  return true;
});